import './userIcon.css'
import {
  type BadgeRole,
  CIRCLE_TIERS,
  GATEKEEPER_TIERS,
  ICONS,
  type IconName,
  PLACE_TIERS,
  type Position,
  type StatusKind,
} from '@feral/shared/client'
import { labelsOf, tooltipOf } from '@feral/shared/options/core'
import {
  POSITION_BADGES,
  POSITION_OPTIONS,
  type PositionBadgeKey,
  positionBadge,
} from '@feral/shared/options/profile'
import { STATUS_KIND_OPTIONS } from '@feral/shared/options/status'
import type { ReactNode } from 'react'
import { Avatar, type Ring, type RingTier } from './identity.js'

/**
 * A user icon, drawn the same in the app and the console (docs/USER-ICONS.md). The app wraps it with
 * your own warning (apps/web people/UserIcon.tsx); the console puts its caution in the same corner.
 */

/** Under this, an icon keeps its ring and its status badge only (docs/USER-ICONS.md IC-R8). */
export const SMALL_ICON_PX = 32

export interface IconState {
  ring: Ring
  tier: RingTier
  /** Anonymous with a vouch, where the viewer was told (decision 172): its words say Eligible */
  eligible?: boolean
  photo: string | null
  status: StatusKind | null
  position: Position | null
  /** a Side's Sucking, for the arrow on their badge */
  sucking?: string | null
  /** for your own warning on them (bottom left, in the app); absent on a silhouette */
  id?: string
  /** the photo blurred until asked (the console, where it isn't their SFW one) */
  blur?: boolean
}

/**
 * Which drawing each standing badge uses (IC-07): in front of a handle in the DOM (Handle), and in a
 * pin's top right corner on the map canvas (apps/web map/pins.ts), which draws no handle. The highest
 * only, but for a profile's: staff in Bone, then each kind of honor in its own color (BADGE_COLOR).
 */
export const ROLE_BADGE: Record<BadgeRole, { icon: IconName; label: string; tip: string }> = {
  owner: { icon: 'crown', label: 'Owner', tip: 'Owner: runs Feral Mode' },
  moderator: { icon: 'shield', label: 'Moderator', tip: 'Moderator: keeps it safe' },
  founder: { icon: 'star', label: 'Founder', tip: 'Founder: one of the first members' },
  guardian: {
    icon: 'gate-3',
    label: 'Guardian',
    tip: `Guardian: vetted ${GATEKEEPER_TIERS[2]} or more people in`,
  },
  legend: {
    icon: 'circle-3',
    label: 'Legend',
    tip: `Legend: ${CIRCLE_TIERS[2]} or more members in their circle`,
  },
  trailblazer: {
    icon: 'flag-3',
    label: 'Trailblazer',
    tip: `Trailblazer: put ${PLACE_TIERS[2]} or more places on the map`,
  },
  sentinel: {
    icon: 'gate-2',
    label: 'Sentinel',
    tip: `Sentinel: vetted ${GATEKEEPER_TIERS[1]} or more people in`,
  },
  well_connected: {
    icon: 'circle-2',
    label: 'Well Connected',
    tip: `Well Connected: ${CIRCLE_TIERS[1]} or more members in their circle`,
  },
  tracker: {
    icon: 'flag-2',
    label: 'Tracker',
    tip: `Tracker: put ${PLACE_TIERS[1]} or more places on the map`,
  },
  gatekeeper: {
    icon: 'gate-1',
    label: 'Gatekeeper',
    tip: `Gatekeeper: vetted ${GATEKEEPER_TIERS[0]} or more people in`,
  },
  connected: {
    icon: 'circle-1',
    label: 'Connected',
    tip: `Connected: ${CIRCLE_TIERS[0]} or more members in their circle`,
  },
  scout: { icon: 'flag-1', label: 'Scout', tip: `Scout: put ${PLACE_TIERS[0]} or more places on the map` },
  builder: { icon: 'hard-hat', label: 'Builder', tip: "Builder: met this month's goal" },
}

/**
 * Each standing's color (IC-07), by kind, levels I to III alike, so a profile's badges read apart
 * (bugs.md). The same as the `--badge-*` tokens (tokens.css), which the DOM uses; this is for the map
 * canvas, which can't read CSS. Change both together.
 */
export const BADGE_COLOR: Record<BadgeRole, string> = {
  owner: '#e6e4df',
  moderator: '#e6e4df',
  founder: '#d4ff00',
  guardian: '#ffc233',
  sentinel: '#ffc233',
  gatekeeper: '#ffc233',
  legend: '#4fc3ff',
  well_connected: '#4fc3ff',
  connected: '#4fc3ff',
  trailblazer: '#b48cff',
  tracker: '#b48cff',
  scout: '#b48cff',
  builder: '#ff8fc8',
}

/** What a presence dot means, where a tile stands in for the ring (IC-01, IC-R4). */
export const PRESENCE_TIP = {
  online: 'Online now',
  recent: 'On in the last half hour',
  stale: 'Not on lately',
} as const

/** What a status badge means, for its tooltip (IC-04). */
export const STATUS_TIP: Partial<Record<StatusKind, string>> = {
  hosting: "Hosting: their door's open",
  looking: 'Looking: now, today or tonight',
  at_spot: 'At a spot: checked in somewhere',
  travelling: 'Traveling: heading somewhere',
}

/** The status badge's drawing (IC-04); browsing has none. */
export function statusIcon(kind: StatusKind | null | undefined): IconName | null {
  if (!kind) return null
  const o = STATUS_KIND_OPTIONS[kind]
  return 'icon' in o ? o.icon : null
}

/**
 * Position badges on every user icon (IC-05, bottom right): line symbols in the spot icons' style, on a
 * 24-unit grid; which drawing is each position's `icon` in options.ts (@feral/shared). The map draws the
 * same paths on a canvas (apps/web map/pins.ts). Arrowheads carry the meaning: up for top, down for
 * bottom, both for vers, the larger head for the side a vers leans to.
 */
export const POSITION_ICON = Object.fromEntries(
  Object.entries(POSITION_BADGES).map(([k, o]) => [k, ICONS[o.icon as IconName]]),
) as Record<PositionBadgeKey, string>

/**
 * A position's badge: a Side who said which way they suck shows that arrow (left receives, right
 * gives, both ways both; positionBadge in @feral/shared), with its words on hover.
 */
export function PositionBadge({
  position,
  sucking,
  size = 16,
}: {
  position: Position
  sucking?: string | null | undefined
  size?: number
}) {
  const key = positionBadge(position, sucking) ?? position
  const glyph = Math.round(size * 0.72)
  return (
    <span
      className="fm-pos"
      role="img"
      aria-label={POSITION_BADGES[key].label}
      title={key === position ? tooltipOf(POSITION_OPTIONS, position) : POSITION_BADGES[key].label}
      style={{ width: size, height: size }}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={POSITION_ICON[key]} />
      </svg>
    </span>
  )
}

const RING_WORDS: Record<Ring, string | null> = { online: 'Online now', recent: 'On recently', stale: null }
const TIER_WORDS: Record<RingTier, string> = {
  trusted: 'Trusted',
  pending: 'Pending',
  anonymous: 'Anonymous',
}
const STATUS_WORDS = labelsOf(STATUS_KIND_OPTIONS)

/**
 * What the icon says, in words, for a screen reader and a tooltip (IC-R1): "Online now, Hosting, Trusted".
 * Their standing is the handle's to say (Handle).
 */
export function iconWords(s: IconState): string {
  return [
    RING_WORDS[s.ring],
    statusIcon(s.status) && s.status ? STATUS_WORDS[s.status] : null,
    s.tier === 'anonymous' && s.eligible ? 'Eligible' : TIER_WORDS[s.tier],
    s.position ? POSITION_BADGES[positionBadge(s.position, s.sucking) ?? s.position].label : null,
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * A badge: a line drawing on a disc, filled for Hosting and Looking. In a corner of a user icon, or in
 * a row (`corner` left out) where a person is a tile.
 */
export function Badge({
  corner,
  icon,
  tone,
  size,
  tip,
}: {
  corner?: 'tl'
  icon: IconName
  tone: string
  size: number
  /** what it means, for its own tooltip (the icon's words cover the whole of it) */
  tip?: string | undefined
}) {
  const glyph = Math.round(size * 0.68)
  return (
    <span
      className={`fm-badge fm-badge--${corner ?? 'inline'} fm-badge--${tone}`}
      style={{ width: size, height: size }}
      data-tip={tip}
    >
      <svg
        width={glyph}
        height={glyph}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={ICONS[icon]} />
      </svg>
    </span>
  )
}

/**
 * A standing's badge in a row (IC-07): its drawing in its color, its tooltip, and its name to a screen
 * reader. In front of a handle (Handle), and in the profile's list of them.
 */
export function StandingBadge({ role, size = 18 }: { role: BadgeRole; size?: number }) {
  const b = ROLE_BADGE[role]
  return (
    <span className="fm-standing" role="img" aria-label={b.label} data-tip={b.tip}>
      <Badge icon={b.icon} tone={`standing fm-badge--${role}`} size={size} />
    </span>
  )
}

/**
 * A handle with their standing in front of it, on one line (IC-R7), the same in the app and the
 * console: the highest one where a row has only that (`role`), every one on a profile (`standings`).
 * The name truncates; the badges never wrap away from it.
 */
export function Handle({
  role,
  size,
  className,
  children,
}: {
  role: BadgeRole | readonly BadgeRole[] | null | undefined
  /** the badges' size, about the name's line height */
  size?: number
  className?: string
  children: ReactNode
}) {
  const roles = !role ? [] : typeof role === 'string' ? [role] : role
  return (
    <span className={className ? `fm-handle-line ${className}` : 'fm-handle-line'}>
      {roles.map((r) => (
        <StandingBadge key={r} role={r} size={size} />
      ))}
      <span className="fm-handle-line__name">{children}</span>
    </span>
  )
}

/** A badge's size on an icon this big. */
export const badgeSize = (size: number) => Math.max(14, Math.round(size * 0.38))

/**
 * A person, read the same everywhere: the ring's color is presence and its line the tier; status top
 * left, position bottom right, and `caution` (your warning in the app, held or removed in the console)
 * bottom left. Their standing goes in front of their handle (Handle), not on the icon. Under 32 px only
 * the ring and the status. The whole of it is one image to a screen reader, named in words.
 */
export function UserIcon({
  s,
  size = 44,
  caution,
}: {
  s: IconState
  size?: number
  /** the bottom left corner, drawn at 32 px and over */
  caution?: ReactNode
}) {
  const small = size < SMALL_ICON_PX
  const badge = badgeSize(size)
  const status = statusIcon(s.status)
  const words = iconWords(s)
  return (
    <span className="fm-icon" role="img" aria-label={words} data-tip={words}>
      <Avatar size={size} ring={s.ring} tier={s.tier} src={s.photo} blur={s.blur} />
      {status && s.status && (
        <Badge corner="tl" icon={status} tone={s.status} size={badge} tip={STATUS_TIP[s.status]} />
      )}
      {!small && s.position && <PositionBadge position={s.position} sucking={s.sucking} size={badge} />}
      {!small && caution}
    </span>
  )
}
